import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, filter, take } from 'rxjs/operators';
import { throwError, BehaviorSubject, race } from 'rxjs';
import { AuthService } from '../auth/auth.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);
const refreshError$ = new BehaviorSubject<boolean>(false);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const token = authService.getAccessToken();

    // Kirim timezone client ke backend untuk konversi timestamp.
    const tzOffset = -new Date().getTimezoneOffset();
    const tzHours = Math.floor(Math.abs(tzOffset) / 60);
    const tzMinutes = Math.abs(tzOffset) % 60;
    const tzSign = tzOffset >= 0 ? '+' : '-';
    const xTimezone = `GMT${tzSign}${String(tzHours).padStart(2, '0')}:${String(tzMinutes).padStart(2, '0')}`;

    req = req.clone({
        setHeaders: { 'X-Timezone': xTimezone },
    });

    if (token) {
        req = req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
        });
    }

    return next(req).pipe(
        catchError((error) => {
            const is401 = error.status === 401 || (error as HttpErrorResponse).error?.code === 'TOKEN_EXPIRED';

            if (is401 && !req.url.includes('/auth/')) {
                if (!isRefreshing) {
                    isRefreshing = true;
                    refreshTokenSubject.next(null);
                    refreshError$.next(false);

                    return authService.refreshToken().pipe(
                        switchMap((response) => {
                            const newToken = response.data?.access_token;
                            if (!newToken) {
                                isRefreshing = false;
                                refreshTokenSubject.next(null);
                                refreshError$.next(true);
                                authService.clearAuth();
                                return throwError(() => new Error('Invalid refresh response'));
                            }
                            isRefreshing = false;
                            refreshTokenSubject.next(newToken);
                            return next(
                                req.clone({
                                    setHeaders: {
                                        Authorization: `Bearer ${newToken}`,
                                    },
                                }),
                            );
                        }),
                        catchError((err) => {
                            isRefreshing = false;
                            refreshTokenSubject.next(null);
                            refreshError$.next(true);
                            authService.clearAuth();
                            return throwError(() => err);
                        }),
                    );
                }

                return race(
                    refreshTokenSubject.pipe(
                        filter((t) => t !== null),
                        take(1),
                        switchMap((t) =>
                            next(
                                req.clone({
                                    setHeaders: { Authorization: `Bearer ${t}` },
                                }),
                            ),
                        ),
                    ),
                    refreshError$.pipe(
                        filter((failed) => failed),
                        take(1),
                        switchMap(() => throwError(() => new Error('Token refresh failed'))),
                    ),
                );
            }

            return throwError(() => error);
        }),
    );
};
